/**
 * Layout primitives — the MUI-free replacements for `Box` and `Stack`.
 *
 * WHY THESE EXIST, AND WHY THEY ARE ONE FILE. The dominant MUI surface in this
 * app is layout: `Box` (337 files), `Stack` (306) and `Typography` (332), and
 * they are also the reason `sx` is everywhere, because an `sx` prop on a layout
 * container was the cheapest way to say "flex row, gap 2". So long as those
 * three had no replacement, `sx` could not leave the tree and neither could
 * anything downstream of it (MUI controls, the MUI theme, emotion). These are
 * the replacements; `ui/text` holds the third. They live in one file for the
 * same reason `ui/form-layout` does: they are one family that shares one
 * spacing scale, and splitting that scale across three files is how a second
 * copy of it gets written.
 *
 * THE SPACING SCALE IS THE MUI UNIT RAMP, VERBATIM — NOT A REM STEP.
 * `theme.spacing` is `minervaDisplay.spacingUnitPx`, i.e. 7 / 8 / 10 px at the
 * small / medium / large display-size preference. These primitives take the SAME
 * number, named `SpaceUnit`, and render exactly that many units:
 * `gap={2}` -> `gap-[calc(var(--minerva-spacing)*2)]` -> 14 / 16 / 20 px. That is
 * byte-for-byte what MUI's `spacing(2)` rendered at all three preferences, and
 * it is `--minerva-spacing` — the same variable `ui/card`'s padding, the menus,
 * the chips and the data-grid cells already read. This is deliberate and it is
 * the whole point: a migrated surface sits beside un-migrated ones for the
 * whole length of the programme, and two ramps that agree only at the medium
 * preference would put every migrated card's rhythm at a different rhythm from
 * its neighbour at small and large. (An earlier revision rendered the Tailwind
 * step `2n` = `0.5n rem`, which is exact at medium but +7.1% at small and -10%
 * at large; design review round 1, D3, and this is the correction.)
 *
 * WHERE REM STEPS ARE STILL RIGHT. `--minerva-spacing` is for values being
 * TRANSLATED from MUI, because it is the ramp the un-migrated surface on the
 * other side of the border still reads. Rem-based Tailwind steps remain for
 * rhythm that has no MUI original to be equal to — `ui/form-layout`'s between
 * field gaps and `ui/field`'s internal label/control/message gaps are new
 * system, not translations, and their docblocks say so. Two spellings, one
 * rule: equal to MUI where a MUI value existed, root-relative where none did.
 *
 * A VALUE OUTSIDE THE UNION IS A NUDGE, NOT A NEW STEP. `SPACE_UNITS` is the
 * closed design scale (the MUI quarter steps the tree actually uses). The tree
 * holds ~95 numeric spacing values that are outside it (`gap={0.85}`,
 * `gap={2.25}` is now in, `mt={0.35}`, ...). The rule for those: snap to the
 * nearest unit in the union, and if the snap visibly moves something, write the
 * exact value on `className` in the SAME ramp —
 * `className="gap-[calc(var(--minerva-spacing)*0.85)]"` — rather than inventing
 * a third spelling. Never convert one of them to a Tailwind step.
 *
 * WHAT IS DELIBERATELY MISSING. Neither primitive is an `sx` engine. There is
 * no `position`, no `border`, no gradient, and no escape hatch that takes a
 * style object — a call site that needs those either composes a real component
 * (`ui/card`, `ui/field`) or passes a Tailwind `className`, which is what the
 * class strings here compose through `cn` so a caller's utility wins on
 * conflict. The parity rule for the migration is therefore: layout props carry
 * the token semantics, `className` carries placement (`w-1/2`, `col-span-2`, a
 * one-off grid track list, `absolute` inset geometry). A computed value no
 * class can carry (a percentage flex basis) goes through React's own `style`
 * prop, which every HTML element already takes.
 *
 * EVERY SPACING PROP TAKES THE RESPONSIVE FORM (`{ base, sm, md }`), because
 * the MUI it replaces did: `sx={{ px: { xs: 0.5, md: 1.25 } }}` and
 * `direction={{ xs: 'column', sm: 'row' }}` both appear in feature code, and a
 * primitive that modelled only half of them would push the other half back into
 * `className` — a second spelling of the same intent, which is the thing this
 * migration is removing. `base` may be omitted, and the component then falls
 * back to its OWN default for that prop (`direction` falls back to `column`,
 * so `<Stack direction={{ md: 'row' }}>` is still a column below 900 rather
 * than a row at every width) — an omitted base is never a silent inversion.
 *
 * `STACK` TAKES `spacing`, NEVER `gap`. `gap` is `Box`'s prop and `Stack` omits
 * it from its own surface on purpose: the two would be the same channel written
 * twice, and because `Box` merges its own classes before the `className` where
 * `Stack` computed them, a `gap` handed to `Stack` would be silently overridden
 * and render ZERO gap. That is a real trap — `gap={n}` is the dominant spelling
 * in the un-migrated tree (1,101 `<Stack … gap={…}>` sites) — so it is a TYPE
 * ERROR here (`layout.test.tsx` asserts it with `@ts-expect-error`), not a
 * silent one. Migrating a `<Stack gap={n}>` means renaming the prop to
 * `spacing={n}`.
 *
 * `sm` AND `md` MEAN MUI'S BREAKPOINTS (600 / 900), NOT TAILWIND'S (640 / 768).
 * A responsive prop here is a translation of a MUI responsive object
 * (`direction={{ xs: 'column', md: 'row' }}`), and MUI's `md` fires at 900px
 * while Tailwind's fires at 768px — so the tables spell the variants
 * `min-[600px]:` / `min-[900px]:` rather than `sm:` / `md:`, and a migrated
 * breakpoint is exact at EVERY width instead of approximately right between
 * 768 and 900. The same holds for a `className` a call site writes by hand:
 * ANY breakpoint that came from a MUI responsive object is
 * `min-[600px]:` / `min-[900px]:` / `max-[599.95px]:`, never the Tailwind
 * prefix, or a grid silently re-times by 40px. This is a PER-CALL-SITE origin
 * rule: importing a primitive does not make every utility in that file a
 * migrated responsive object. Native Tailwind breakpoints remain legitimate;
 * the former import-triggered CI census was retired because it established no
 * actual geometry defect. Keep responsive behaviour tied to its real surface
 * and viewport evidence rather than rewriting unrelated utilities.
 *
 * `FormStack` is NOT replaced by `Stack` and the two are not alternatives.
 * `FormStack` owns the between-FIELD rhythm of a form (`ui/field` fixes the
 * space inside a field and both are asserted by `scripts/check-field-atom-
 * geometry.mjs`); `Stack` is the general one-dimensional layout container for
 * everything that is not a form's field rhythm. A form keeps using FormStack.
 */

'use client';

import * as React from 'react';

import { cn } from './cn';
import { TEXT_COLOR_CLASS, type TextColor } from './text';

/**
 * The MUI spacing unit, kept as the API's vocabulary so a migrated call site
 * keeps the number it already had, and rendered at that number of units
 * (`calc(var(--minerva-spacing) * n)` — see the docblock). A value outside this
 * union goes on `className` in the same ramp; never convert it to a step.
 */
export const SPACE_UNITS = [
  0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.5, 3.75, 4,
  4.25, 4.5, 5, 6,
] as const;

export type SpaceUnit = (typeof SPACE_UNITS)[number];

/** The three variant classes a table row carries (see the breakpoint note). */
type ResponsiveClass = { base?: string; sm: string; md: string };

const GAP_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'gap-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'gap-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'gap-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'gap-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'gap-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'gap-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'gap-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'gap-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'gap-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'gap-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'gap-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'gap-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'gap-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'gap-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'gap-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'gap-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'gap-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'gap-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'gap-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'gap-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:gap-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:gap-[calc(var(--minerva-spacing)*6)]',
  },
};

const P_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'p-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'p-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'p-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'p-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'p-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'p-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'p-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'p-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'p-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'p-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'p-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'p-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'p-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'p-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'p-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'p-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'p-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'p-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'p-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'p-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:p-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:p-[calc(var(--minerva-spacing)*6)]',
  },
};

const PX_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'px-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'px-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'px-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'px-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'px-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'px-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'px-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'px-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'px-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'px-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'px-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'px-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'px-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'px-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'px-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'px-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'px-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'px-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'px-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'px-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:px-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:px-[calc(var(--minerva-spacing)*6)]',
  },
};

const PY_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'py-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'py-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'py-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'py-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'py-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'py-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'py-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'py-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'py-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'py-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'py-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'py-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'py-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'py-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'py-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'py-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'py-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'py-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'py-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'py-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:py-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:py-[calc(var(--minerva-spacing)*6)]',
  },
};

const PT_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'pt-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'pt-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'pt-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'pt-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'pt-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'pt-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'pt-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'pt-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'pt-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'pt-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'pt-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'pt-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'pt-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'pt-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'pt-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'pt-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'pt-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'pt-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'pt-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'pt-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:pt-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:pt-[calc(var(--minerva-spacing)*6)]',
  },
};

const PR_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'pr-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'pr-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'pr-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'pr-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'pr-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'pr-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'pr-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'pr-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'pr-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'pr-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'pr-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'pr-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'pr-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'pr-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'pr-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'pr-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'pr-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'pr-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'pr-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'pr-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:pr-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:pr-[calc(var(--minerva-spacing)*6)]',
  },
};

const PB_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'pb-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'pb-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'pb-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'pb-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'pb-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'pb-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'pb-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'pb-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'pb-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'pb-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'pb-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'pb-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'pb-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'pb-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'pb-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'pb-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'pb-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'pb-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'pb-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'pb-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:pb-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:pb-[calc(var(--minerva-spacing)*6)]',
  },
};

const PL_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'pl-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'pl-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'pl-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'pl-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'pl-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'pl-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'pl-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'pl-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'pl-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'pl-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'pl-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'pl-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'pl-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'pl-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'pl-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'pl-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'pl-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'pl-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'pl-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'pl-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:pl-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:pl-[calc(var(--minerva-spacing)*6)]',
  },
};

const M_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'm-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'm-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'm-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'm-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'm-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'm-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'm-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'm-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'm-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'm-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'm-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'm-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'm-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'm-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'm-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'm-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'm-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'm-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'm-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'm-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:m-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:m-[calc(var(--minerva-spacing)*6)]',
  },
};

const MX_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'mx-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'mx-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'mx-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'mx-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'mx-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'mx-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'mx-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'mx-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'mx-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'mx-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'mx-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'mx-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'mx-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'mx-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'mx-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'mx-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'mx-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'mx-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'mx-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'mx-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:mx-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:mx-[calc(var(--minerva-spacing)*6)]',
  },
};

const MY_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'my-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'my-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'my-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'my-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'my-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'my-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'my-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'my-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'my-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'my-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'my-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'my-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'my-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'my-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'my-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'my-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'my-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'my-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'my-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'my-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:my-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:my-[calc(var(--minerva-spacing)*6)]',
  },
};

const MT_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'mt-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'mt-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'mt-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'mt-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'mt-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'mt-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'mt-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'mt-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'mt-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'mt-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'mt-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'mt-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'mt-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'mt-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'mt-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'mt-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'mt-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'mt-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'mt-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'mt-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:mt-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:mt-[calc(var(--minerva-spacing)*6)]',
  },
};

const MR_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'mr-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'mr-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'mr-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'mr-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'mr-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'mr-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'mr-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'mr-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'mr-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'mr-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'mr-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'mr-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'mr-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'mr-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'mr-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'mr-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'mr-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'mr-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'mr-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'mr-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:mr-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:mr-[calc(var(--minerva-spacing)*6)]',
  },
};

const MB_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'mb-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'mb-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'mb-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'mb-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'mb-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'mb-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'mb-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'mb-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'mb-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'mb-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'mb-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'mb-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'mb-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'mb-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'mb-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'mb-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'mb-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'mb-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'mb-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'mb-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:mb-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:mb-[calc(var(--minerva-spacing)*6)]',
  },
};

const ML_CLASS: Record<SpaceUnit, ResponsiveClass> = {
  0: {
    base: 'ml-[calc(var(--minerva-spacing)*0)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*0)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*0)]',
  },
  0.25: {
    base: 'ml-[calc(var(--minerva-spacing)*0.25)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*0.25)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*0.25)]',
  },
  0.5: {
    base: 'ml-[calc(var(--minerva-spacing)*0.5)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*0.5)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*0.5)]',
  },
  0.75: {
    base: 'ml-[calc(var(--minerva-spacing)*0.75)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*0.75)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*0.75)]',
  },
  1: {
    base: 'ml-[calc(var(--minerva-spacing)*1)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*1)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*1)]',
  },
  1.25: {
    base: 'ml-[calc(var(--minerva-spacing)*1.25)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*1.25)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*1.25)]',
  },
  1.5: {
    base: 'ml-[calc(var(--minerva-spacing)*1.5)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*1.5)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*1.5)]',
  },
  1.75: {
    base: 'ml-[calc(var(--minerva-spacing)*1.75)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*1.75)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*1.75)]',
  },
  2: {
    base: 'ml-[calc(var(--minerva-spacing)*2)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*2)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*2)]',
  },
  2.25: {
    base: 'ml-[calc(var(--minerva-spacing)*2.25)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*2.25)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*2.25)]',
  },
  2.5: {
    base: 'ml-[calc(var(--minerva-spacing)*2.5)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*2.5)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*2.5)]',
  },
  2.75: {
    base: 'ml-[calc(var(--minerva-spacing)*2.75)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*2.75)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*2.75)]',
  },
  3: {
    base: 'ml-[calc(var(--minerva-spacing)*3)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*3)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*3)]',
  },
  3.5: {
    base: 'ml-[calc(var(--minerva-spacing)*3.5)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*3.5)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*3.5)]',
  },
  3.75: {
    base: 'ml-[calc(var(--minerva-spacing)*3.75)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*3.75)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*3.75)]',
  },
  4: {
    base: 'ml-[calc(var(--minerva-spacing)*4)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*4)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*4)]',
  },
  4.25: {
    base: 'ml-[calc(var(--minerva-spacing)*4.25)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*4.25)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*4.25)]',
  },
  4.5: {
    base: 'ml-[calc(var(--minerva-spacing)*4.5)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*4.5)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*4.5)]',
  },
  5: {
    base: 'ml-[calc(var(--minerva-spacing)*5)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*5)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*5)]',
  },
  6: {
    base: 'ml-[calc(var(--minerva-spacing)*6)]',
    sm: 'min-[600px]:ml-[calc(var(--minerva-spacing)*6)]',
    md: 'min-[900px]:ml-[calc(var(--minerva-spacing)*6)]',
  },
};

/** A single value, or the responsive object the MUI prop took. */
export type Responsive<T> = T | { base?: T; sm?: T; md?: T };

export type FlexDirection = 'row' | 'column';
export type FlexAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
export type FlexJustify =
  | 'start'
  | 'center'
  | 'end'
  | 'between'
  | 'around'
  | 'evenly';

const DIRECTION_CLASS: Record<FlexDirection, ResponsiveClass> = {
  row: {
    base: 'flex-row',
    sm: 'min-[600px]:flex-row',
    md: 'min-[900px]:flex-row',
  },
  column: {
    base: 'flex-col',
    sm: 'min-[600px]:flex-col',
    md: 'min-[900px]:flex-col',
  },
};

const ALIGN_CLASS: Record<FlexAlign, ResponsiveClass> = {
  start: {
    base: 'items-start',
    sm: 'min-[600px]:items-start',
    md: 'min-[900px]:items-start',
  },
  center: {
    base: 'items-center',
    sm: 'min-[600px]:items-center',
    md: 'min-[900px]:items-center',
  },
  end: {
    base: 'items-end',
    sm: 'min-[600px]:items-end',
    md: 'min-[900px]:items-end',
  },
  stretch: {
    base: 'items-stretch',
    sm: 'min-[600px]:items-stretch',
    md: 'min-[900px]:items-stretch',
  },
  baseline: {
    base: 'items-baseline',
    sm: 'min-[600px]:items-baseline',
    md: 'min-[900px]:items-baseline',
  },
};

const JUSTIFY_CLASS: Record<FlexJustify, ResponsiveClass> = {
  start: {
    base: 'justify-start',
    sm: 'min-[600px]:justify-start',
    md: 'min-[900px]:justify-start',
  },
  center: {
    base: 'justify-center',
    sm: 'min-[600px]:justify-center',
    md: 'min-[900px]:justify-center',
  },
  end: {
    base: 'justify-end',
    sm: 'min-[600px]:justify-end',
    md: 'min-[900px]:justify-end',
  },
  between: {
    base: 'justify-between',
    sm: 'min-[600px]:justify-between',
    md: 'min-[900px]:justify-between',
  },
  around: {
    base: 'justify-around',
    sm: 'min-[600px]:justify-around',
    md: 'min-[900px]:justify-around',
  },
  evenly: {
    base: 'justify-evenly',
    sm: 'min-[600px]:justify-evenly',
    md: 'min-[900px]:justify-evenly',
  },
};

/**
 * Resolve a `Responsive<T>` prop against its class table. A bare value renders
 * its base class. An object renders `base` (or `fallback`'s base class when the
 * object omits `base`, so an omitted base can never invert a component's
 * default) plus whichever breakpoint variants it names. The key type is
 * `string | number` because the spacing scale's keys are numbers (`gap={2}`)
 * while the flex vocabularies' are words (`direction="row"`).
 */
function responsiveClasses<K extends string | number>(
  value: Responsive<K> | undefined,
  table: Record<K, ResponsiveClass>,
  fallback?: K
): string {
  if (value === undefined) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return table[value].base ?? '';
  }
  const base = value.base ?? fallback;
  return [
    base === undefined ? '' : (table[base].base ?? ''),
    value.sm === undefined ? '' : table[value.sm].sm,
    value.md === undefined ? '' : table[value.md].md,
  ]
    .filter(Boolean)
    .join(' ');
}

const DISPLAY_CLASS = {
  block: 'block',
  'inline-block': 'inline-block',
  flex: 'flex',
  'inline-flex': 'inline-flex',
  grid: 'grid',
  none: 'hidden',
} as const;

/**
 * Background colour from the token bridge. `error` is the error FILL
 * (`--color-destructive` = `error.main`), which is the right stop for a fill
 * even though it is the wrong stop for error TEXT — that is
 * `--color-destructive-text`, reachable through `Text color="error"`.
 * `neutral` is the scorecard bands' "no band" colour, `text.secondary`, which
 * the bridge exposes as `--color-muted-foreground`.
 */
export const BOX_BG_CLASS = {
  background: 'bg-background',
  card: 'bg-card',
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-destructive',
  info: 'bg-info',
  neutral: 'bg-muted-foreground',
  inherit: '',
} as const;

export type BoxBg = keyof typeof BOX_BG_CLASS;

export interface BoxProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  /** The element to render. `div` unless the semantics demand otherwise. */
  as?: React.ElementType;
  /** Block/grid/flex container display. Omitted leaves the UA default. */
  display?: 'block' | 'inline-block' | 'flex' | 'inline-flex' | 'grid' | 'none';
  /**
   * Text colour from the token bridge. `error` is the AA-safe error ink
   * (`--color-destructive-text`), not `error.main`, which is a fill that fails
   * small-text contrast; `disabled` is `--minerva-palette-text-disabled`.
   */
  color?: TextColor;
  /** Background colour from the token bridge (see `BOX_BG_CLASS`). */
  bg?: BoxBg;
  gap?: Responsive<SpaceUnit>;
  p?: Responsive<SpaceUnit>;
  px?: Responsive<SpaceUnit>;
  py?: Responsive<SpaceUnit>;
  pt?: Responsive<SpaceUnit>;
  pr?: Responsive<SpaceUnit>;
  pb?: Responsive<SpaceUnit>;
  pl?: Responsive<SpaceUnit>;
  m?: Responsive<SpaceUnit>;
  mx?: Responsive<SpaceUnit>;
  my?: Responsive<SpaceUnit>;
  mt?: Responsive<SpaceUnit>;
  mr?: Responsive<SpaceUnit>;
  mb?: Responsive<SpaceUnit>;
  ml?: Responsive<SpaceUnit>;
}

/**
 * A token-driven container. It carries no default layout of its own: every
 * class it renders comes from a prop, so an unchanged `Box` is an unchanged
 * `div` and a migration cannot introduce layout by accident.
 */
export const Box = React.forwardRef<HTMLElement, BoxProps>(function Box(
  {
    as: Component = 'div',
    bg,
    className,
    color,
    display,
    gap,
    m,
    mb,
    ml,
    mr,
    mt,
    mx,
    my,
    p,
    pb,
    pl,
    pr,
    pt,
    px,
    py,
    ...rest
  },
  ref
) {
  return (
    <Component
      ref={ref as React.Ref<never>}
      className={cn(
        'minerva-box',
        display ? DISPLAY_CLASS[display] : '',
        responsiveClasses(gap, GAP_CLASS),
        responsiveClasses(p, P_CLASS),
        responsiveClasses(px, PX_CLASS),
        responsiveClasses(py, PY_CLASS),
        responsiveClasses(pt, PT_CLASS),
        responsiveClasses(pr, PR_CLASS),
        responsiveClasses(pb, PB_CLASS),
        responsiveClasses(pl, PL_CLASS),
        responsiveClasses(m, M_CLASS),
        responsiveClasses(mx, MX_CLASS),
        responsiveClasses(my, MY_CLASS),
        responsiveClasses(mt, MT_CLASS),
        responsiveClasses(mr, MR_CLASS),
        responsiveClasses(mb, MB_CLASS),
        responsiveClasses(ml, ML_CLASS),
        color ? TEXT_COLOR_CLASS[color] : '',
        bg ? BOX_BG_CLASS[bg] : '',
        className
      )}
      {...rest}
    />
  );
});
Box.displayName = 'Box';

/**
 * `StackProps` omits `display` (always `flex`) and `gap` (see the docblock:
 * `spacing` is the single spacing channel, and a `gap` prop here would be
 * silently overridden).
 */
export interface StackProps extends Omit<BoxProps, 'display' | 'gap'> {
  /** `column` (the flex default MUI Stack used) unless stated. */
  direction?: Responsive<FlexDirection>;
  /** The gap between direct children, in MUI spacing units. */
  spacing?: Responsive<SpaceUnit>;
  align?: Responsive<FlexAlign>;
  justify?: Responsive<FlexJustify>;
  /** Children wrap onto the next line instead of compressing. */
  wrap?: boolean;
}

/**
 * A one-dimensional flex container: `Box` with `display="flex"` fixed, plus the
 * flex vocabulary. The padding/margin/colour props come from `Box`, so a
 * `Stack` that needs `paddingTop={1}` has somewhere to put it without falling
 * back to `sx`. `spacing` is a flex `gap`, not MUI's margin-on-children
 * polyfill: the same space between rendered siblings, and none around a
 * conditionally-absent one.
 */
export const Stack = React.forwardRef<HTMLElement, StackProps>(function Stack(
  { align, className, direction = 'column', justify, spacing, wrap, ...rest },
  ref
) {
  return (
    <Box
      {...rest}
      className={cn(
        'minerva-stack',
        // `'column'` as the fallback base: an object that names only `sm`/`md`
        // must not lose Stack's column default, because with `display: flex`
        // and no `flex-direction` the UA default is `row` at every width.
        responsiveClasses(direction, DIRECTION_CLASS, 'column'),
        responsiveClasses(align, ALIGN_CLASS),
        responsiveClasses(justify, JUSTIFY_CLASS),
        wrap ? 'flex-wrap' : '',
        className
      )}
      display="flex"
      gap={spacing}
      ref={ref}
    />
  );
});
Stack.displayName = 'Stack';
